import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { SubirImagen } from '@/components/SubirImagen';
import { useOpcionesLicencia } from '@/features/alumnos/queries';
import { env } from '@/lib/env';
import { fechaLarga, mxn } from '@/lib/formato';
import { cn } from '@/lib/utils';

import { ESTADO_PROMO, useBecas, type Beca, type BecaBody, type Criterios } from './queries';

const GENEROS: Record<string, string> = { M: 'Mujer', H: 'Hombre', N: 'No binario' };

function describir(c: Criterios): string {
  const partes = [
    c.university && `Universidad: ${c.university}`,
    c.minGrade && `Promedio ≥ ${c.minGrade}`,
    c.enarmAttempts !== undefined && `Intentos ENARM: ${c.enarmAttempts}`,
    c.gender && `Género: ${GENEROS[c.gender]}`,
    c.specialty && `Especialidad: ${c.specialty}`,
    c.requiredMembershipId && `Tuvo la licencia #${c.requiredMembershipId}`,
  ].filter(Boolean);
  return partes.length ? partes.join(' · ') : 'Sin criterios (cualquier alumno)';
}

function FormBeca({ actual, onListo }: { actual: Beca | null; onListo: () => void }) {
  const qc = useQueryClient();
  const opciones = useOpcionesLicencia();
  const [f, setF] = useState<BecaBody>(
    actual
      ? {
          membershipId: actual.membershipId,
          name: actual.name,
          criteria: actual.criteria,
          price: actual.price,
          finalPrice: actual.finalPrice,
          startsOn: actual.startsOn,
          endsOn: actual.endsOn,
          image: actual.image,
          imageAlt: actual.imageAlt,
        }
      : { membershipId: 0, name: '', criteria: {}, finalPrice: '' },
  );
  const [poblacion, setPoblacion] = useState<number | null>(null);
  const poner = (c: Partial<BecaBody>) => setF((v) => ({ ...v, ...c }));
  const crit = f.criteria ?? {};
  const ponerCrit = (c: Partial<Criterios>) => {
    const next: Criterios = { ...crit, ...c };
    (Object.keys(next) as (keyof Criterios)[]).forEach((k) => {
      if (next[k] === '' || next[k] === undefined || Number.isNaN(next[k])) delete next[k];
    });
    poner({ criteria: next });
    setPoblacion(null);
  };
  const estimar = useMutation({
    mutationFn: () =>
      unwrap<{ population: number }>(
        api.POST('/api/v1/admin/scholarships/estimate', {
          body: { criteria: crit, ...(f.membershipId ? { membershipId: f.membershipId } : {}) },
        }),
      ),
    onSuccess: (r) => setPoblacion(r.population),
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const guardar = useMutation({
    mutationFn: () =>
      actual
        ? unwrap(
            api.PATCH('/api/v1/admin/scholarships/{id}', {
              params: { path: { id: String(actual.id) } },
              body: f,
            }),
          )
        : unwrap(api.POST('/api/v1/admin/scholarships', { body: f })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tienda', 'becas'] });
      toast.success(actual ? 'Beca guardada.' : 'Beca creada.');
      onListo();
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Licencia">
          <select
            className="input-vidrio"
            value={f.membershipId || ''}
            required
            onChange={(e) => {
              poner({ membershipId: Number(e.target.value) });
              setPoblacion(null);
            }}
          >
            <option value="">Elige una licencia…</option>
            {opciones.data?.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} · {mxn(o.price)}
              </option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta="Nombre">
          <input
            className="input-vidrio"
            value={f.name}
            required
            minLength={2}
            maxLength={50}
            onChange={(e) => poner({ name: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Precio con beca (MXN)">
          <input
            className="input-vidrio"
            inputMode="decimal"
            required
            value={f.finalPrice}
            onChange={(e) => poner({ finalPrice: e.target.value })}
          />
        </Campo>
        <div />
        <Campo etiqueta="Vigente desde">
          <input
            type="date"
            className="input-vidrio"
            value={f.startsOn ?? ''}
            onChange={(e) => poner({ startsOn: e.target.value || null })}
          />
        </Campo>
        <Campo etiqueta="Vigente hasta">
          <input
            type="date"
            className="input-vidrio"
            value={f.endsOn ?? ''}
            onChange={(e) => poner({ endsOn: e.target.value || null })}
          />
        </Campo>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SubirImagen
          destino="beca"
          valor={f.image}
          urlDe={(n) => `${env.cdnUrl}/src/content/licencias/${n}`}
          onCambio={(image) => poner({ image })}
        />
        <SubirImagen
          destino="beca"
          etiqueta="Segunda imagen"
          valor={f.imageAlt}
          urlDe={(n) => `${env.cdnUrl}/src/content/licencias/${n}`}
          onCambio={(imageAlt) => poner({ imageAlt })}
        />
      </div>
      <fieldset className="space-y-3 rounded-2xl border border-[var(--separador)] p-4">
        <legend className="px-1 text-sm font-extrabold">
          Criterios (deja vacío lo que no aplique)
        </legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etiqueta="Universidad">
            <input
              className="input-vidrio"
              value={crit.university ?? ''}
              onChange={(e) => ponerCrit({ university: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Promedio mínimo">
            <input
              className="input-vidrio"
              inputMode="decimal"
              value={crit.minGrade ?? ''}
              onChange={(e) =>
                ponerCrit({ minGrade: e.target.value === '' ? undefined : Number(e.target.value) })
              }
            />
          </Campo>
          <Campo etiqueta="Intentos de ENARM">
            <input
              className="input-vidrio"
              inputMode="numeric"
              value={crit.enarmAttempts ?? ''}
              onChange={(e) =>
                ponerCrit({
                  enarmAttempts: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
          </Campo>
          <Campo etiqueta="Género">
            <select
              className="input-vidrio"
              value={crit.gender ?? ''}
              onChange={(e) =>
                ponerCrit({ gender: (e.target.value || undefined) as Criterios['gender'] })
              }
            >
              <option value="">Cualquiera</option>
              <option value="M">Mujer</option>
              <option value="H">Hombre</option>
              <option value="N">No binario</option>
            </select>
          </Campo>
          <Campo etiqueta="Especialidad objetivo">
            <input
              className="input-vidrio"
              value={crit.specialty ?? ''}
              onChange={(e) => ponerCrit({ specialty: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Debe haber tenido la licencia">
            <select
              className="input-vidrio"
              value={crit.requiredMembershipId ?? ''}
              onChange={(e) =>
                ponerCrit({
                  requiredMembershipId: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            >
              <option value="">No aplica</option>
              {opciones.data?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Campo>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-cs btn-contorno"
            onClick={() => estimar.mutate()}
            disabled={estimar.isPending}
          >
            <Users size={16} aria-hidden /> Estimar población
          </button>
          {poblacion !== null && (
            <p className="text-sm">
              <strong>{poblacion.toLocaleString('es-MX')}</strong> alumnos cumplen los criterios y
              aún no tienen la licencia.
            </p>
          )}
        </div>
      </fieldset>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-cs btn-contorno" onClick={onListo}>
          Cancelar
        </button>
        <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
          Guardar
        </button>
      </div>
    </form>
  );
}

function Becas() {
  const q = useBecas();
  const editable = useCan('tienda', 'editar');
  const qc = useQueryClient();
  const [editando, setEditando] = useState<Beca | 'nueva' | null>(null);
  const [borrando, setBorrando] = useState<Beca | null>(null);
  const borrar = useMutation({
    mutationFn: (id: number) =>
      unwrap<{ result: string }>(
        api.DELETE('/api/v1/admin/scholarships/{id}', { params: { path: { id: String(id) } } }),
      ),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['tienda', 'becas'] });
      setBorrando(null);
      toast.success(
        r.result === 'deleted' ? 'Beca eliminada.' : 'La beca ya tenía pagos: la terminamos hoy.',
      );
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <>
      <Encabezado
        titulo="Becas especiales"
        descripcion="Precio especial para alumnos que cumplen ciertos criterios."
        acciones={
          editable && (
            <button
              type="button"
              className="btn-cs btn-primario"
              onClick={() => setEditando('nueva')}
            >
              <Plus size={16} aria-hidden /> Nueva beca
            </button>
          )
        }
      />
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && <Vacio titulo="Sin becas" />}
      {q.data && q.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Beca</th>
                <th>Licencia</th>
                <th>Precio</th>
                <th>Criterios</th>
                <th>Vigencia</th>
                <th>Usos</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map((b) => {
                const [texto, clase] = ESTADO_PROMO[b.status] ?? ['', ''];
                return (
                  <tr key={b.id}>
                    <td className="font-bold">{b.name}</td>
                    <td>{b.membershipName}</td>
                    <td className="tabular whitespace-nowrap">{mxn(b.finalPrice)}</td>
                    <td className="max-w-sm text-xs">{describir(b.criteria)}</td>
                    <td className="whitespace-nowrap">
                      {b.startsOn ? fechaLarga(b.startsOn) : '—'} –{' '}
                      {b.endsOn ? fechaLarga(b.endsOn) : 'sin fin'}
                    </td>
                    <td className="tabular">{b.uses}</td>
                    <td>
                      <span className={cn('insignia', clase)}>{texto}</span>
                    </td>
                    <td className="space-x-2 text-right whitespace-nowrap">
                      {editable && (
                        <>
                          <button
                            type="button"
                            className="btn-cs btn-contorno px-2 py-1 text-xs"
                            onClick={() => setEditando(b)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn-cs btn-peligro px-2 py-1 text-xs"
                            onClick={() => setBorrando(b)}
                          >
                            Eliminar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        abierto={editando !== null}
        onCerrar={() => setEditando(null)}
        titulo={editando === 'nueva' ? 'Nueva beca' : `Editar ${editando?.name ?? ''}`}
        ancho="max-w-3xl"
      >
        {editando !== null && (
          <FormBeca
            key={editando === 'nueva' ? 'nueva' : editando.id}
            actual={editando === 'nueva' ? null : editando}
            onListo={() => setEditando(null)}
          />
        )}
      </Modal>
      <ConfirmarAccion
        abierto={borrando !== null}
        onCerrar={() => setBorrando(null)}
        titulo={`Eliminar ${borrando?.name ?? ''}`}
        descripcion="Si alguien ya pagó con esta beca no se borra: se termina hoy para conservar el historial."
        textoBoton="Eliminar"
        peligro
        ocupado={borrar.isPending}
        onConfirmar={() => borrando && borrar.mutate(borrando.id)}
      />
    </>
  );
}

export function BecasPage() {
  return (
    <PermissionGate seccion="tienda">
      <Becas />
    </PermissionGate>
  );
}
